import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FacilityDictionaryService } from './facility-dictionary.service';
import { combineLatest, map, scan, shareReplay, startWith, tap } from 'rxjs';
import { IDictionaryItem } from '../../types/dictionary-item.interface';
import { AsyncPipe } from '@angular/common';
import { MultiselectComponent } from '@ui-lib/components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'mon-facility-selector',
  imports: [TranslateModule, FormsModule, AsyncPipe, MultiselectComponent, ReactiveFormsModule],
  templateUrl: './facility-selector.component.html',
  styleUrl: './facility-selector.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FacilityDictionaryService],
})
export class FacilitySelectorComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  protected readonly facilityDictionaryService = inject(FacilityDictionaryService);

  @Input()
  public controlEntity: FormControl<string[]> = new FormControl([], { nonNullable: true });

  @Output()
  public selectionChange: EventEmitter<IDictionaryItem<string>[]> = new EventEmitter<
    IDictionaryItem<string>[]
  >();

  private loadedCount = 0;

  protected readonly facilities$ = this.facilityDictionaryService.source$.pipe(
    scan(
      (
        acc: IDictionaryItem<string>[],
        newbies: IDictionaryItem<string>[],
      ): IDictionaryItem<string>[] => [...acc, ...newbies],
      [],
    ),
    map((items: IDictionaryItem<string>[]): IDictionaryItem<string>[] => {
      const seenIds = new Set<string>();

      return items.filter((item: IDictionaryItem<string>): boolean => {
        if (!item.id) return false;

        if (seenIds.has(item.id)) {
          return false;
        }

        seenIds.add(item.id);

        return true;
      });
    }),
    tap((items: IDictionaryItem<string>[]): void => {
      this.loadedCount = items.length;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public ngOnInit(): void {
    combineLatest([
      this.controlEntity.valueChanges.pipe(startWith(this.controlEntity.value)),
      this.facilities$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([selectedIds, items]) => {
        const selectedIdsSet = new Set(selectedIds);
        const selectedItems = items.filter((item) => selectedIdsSet.has(item.id));

        this.selectionChange.emit(selectedItems);

        // Выбор может прийти не из списка, а извне — например, из ссылки с карточки площадки.
        // Площадки, которых ещё нет на загруженных страницах словаря, догружаем: иначе у выбора
        // не будет названия.
        if (selectedItems.length < selectedIdsSet.size) {
          this.loadMore();
        }
      });
  }

  public loadMore(): void {
    if (this.loadedCount < this.facilityDictionaryService.totalCount()) {
      this.facilityDictionaryService.loadMore();
    }
  }
}
